import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const AccuracyStats = ({ accuracyData, performanceMetrics }) => {
  const COLORS = ['#38A169', '#D69E2E', '#E53E3E'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-sm font-medium text-popover-foreground">
            Accuracy: {payload?.[0]?.value}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="BarChart3" size={24} className="text-primary" />
        <h3 className="text-xl font-semibold text-card-foreground">Prediction Accuracy</h3>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Overall Stats */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-card-foreground">Overall Performance</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Target" size={16} className="text-success" />
                <span className="text-sm font-medium text-muted-foreground">Success Rate</span>
              </div>
              <p className="text-2xl font-bold text-success">{performanceMetrics?.successRate}%</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Avg Deviation</span>
              </div>
              <p className="text-2xl font-bold text-card-foreground">{performanceMetrics?.avgDeviation}%</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Calendar" size={16} className="text-secondary" />
                <span className="text-sm font-medium text-muted-foreground">Total Predictions</span>
              </div>
              <p className="text-2xl font-bold text-card-foreground">{performanceMetrics?.totalPredictions}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={16} className="text-accent" />
                <span className="text-sm font-medium text-muted-foreground">Avg Time</span>
              </div>
              <p className="text-2xl font-bold text-card-foreground">{performanceMetrics?.avgTimeframe}</p>
            </div>
          </div>
        </div>

        {/* Accuracy Distribution */}
        <div>
          <h4 className="text-lg font-medium text-card-foreground mb-4">Accuracy Distribution</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'High (>90%)', value: performanceMetrics?.highAccuracy, color: '#38A169' },
                    { name: 'Medium (70-90%)', value: performanceMetrics?.mediumAccuracy, color: '#D69E2E' },
                    { name: 'Low (<70%)', value: performanceMetrics?.lowAccuracy, color: '#E53E3E' }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {[
                    { name: 'High (>90%)', value: performanceMetrics?.highAccuracy, color: '#38A169' },
                    { name: 'Medium (70-90%)', value: performanceMetrics?.mediumAccuracy, color: '#D69E2E' },
                    { name: 'Low (<70%)', value: performanceMetrics?.lowAccuracy, color: '#E53E3E' }
                  ]?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center space-x-4 mt-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-sm text-muted-foreground">High</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span className="text-sm text-muted-foreground">Medium</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <span className="text-sm text-muted-foreground">Low</span>
            </div>
          </div>
        </div>
      </div>
      {/* Historical Accuracy Trend */}
      <div>
        <h4 className="text-lg font-medium text-card-foreground mb-4">Accuracy Trend (Last 30 Days)</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={accuracyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="accuracy" 
                fill="var(--color-primary)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h5 className="font-medium text-card-foreground mb-1">How We Calculate Accuracy</h5>
            <p className="text-sm text-muted-foreground">
              Accuracy is measured by comparing predicted price targets with actual market prices at the specified timeframe. 
              High accuracy predictions are within 5% of the target, medium within 15%, and low accuracy beyond 15% deviation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccuracyStats;